import React, { useState } from "react";
import classNames from "classnames/bind";
import { Form, Field } from "react-final-form";
import useSWR from "swr";

import { CATEGORIES } from "../../api/categories";

import { Screen } from "../../components/Screen";
import { Input } from "../../components/Input";
import { InputDate } from "../../components/InputDate";
import { InputPhone } from "../../components/InputPhone";
import { RadioGroup } from "../../components/RadioGroup";
import { MobileSelect } from "../../components/MobileSelect";
import { AnthropometryFields } from "components/AnthropometryFields";
import { PageLoader } from "components/PageLoader";

import { useStateManager } from "utils/useStateManager";

import { UserType } from "types/UserType";

import { createValidation, epharmacyFormValidator } from "./validation";

import styles from "./Registration.module.css";

const cx = classNames.bind(styles);

const radioGroupSex = [
  {
    value: "1",
    label: "Мужской",
  },
  {
    value: "2",
    label: "Женский",
  },
];

export enum Smoking {
  YES = "YES",
  NO = "NO",
}

export type FormValues = {
  custom_lastname: string;
  custom_firstname: string;
  custom_midname: string;
  custom_дата_рождения: string;
  custom_sexint_save: string;
  custom_login: string;
  custom_телефон: string;
  custom_страна?: string;
  custom_город?: string;
  custom_адрес: string;
  custom_масса_тела: string;
  custom_рост: string;
  custom_детская_жизненная_активность?: string;
  custom_жизненная_активность?: string;
  custom_спортивная_автивность?: string;
  smoking: Smoking;
  /*Для последнего экрана скачивания отчёта*/
  custom_email_for_pdf: string;
};

export type FormEmailAndPhoheScreenValues = {
  custom_login?: string;
  custom_phone?: string;
}

type Props = {
  onSubmit: (exists?: boolean) => void;
};

type FormFieldProps = React.ComponentProps<typeof Input> & {
  name: keyof FormValues;
};

export type FieldsConfig = {
  [key in fields]: FieldConfig;
};

type fields = 'name' | 'lastname' | 'midname' | 'phone' | 'email' | 'birthday' | 'gender' | 'country' | 'city' | 'address'

type FieldConfig = {
  isRequired?: boolean;
  isVisible: boolean;
};

const FormField: React.FC<FormFieldProps> = (props) => (
  <Field name={props.name}>
    {({ input, meta }) => (
      <Input
        {...input}
        label={props.label}
        required={props.required}
        mask={props.mask}
        error={meta.touched ? meta.error : undefined}
      />
    )}
  </Field>
);

const calculateAge = (birthDay: number, birthMonth: number, birthYear: number) => {
  const nuw = new Date();
  const currentYear = nuw.getFullYear();

  let age = currentYear - birthYear;

  return age;
}
const getIsChild = (dateString: string) => {
  const parts = dateString.split(".").map((item) => parseInt(item, 10)) as [
    number,
    number,
    number
  ];

  const age = calculateAge(...parts);

  return age < 18;
};

export const Registration: React.FC<Props> = (props) => {
  const { userType, linkInfo, patient, updatePatient, isLoading, isInitial , isRegistrationDone, setIsRegistrationDone} = useStateManager();
  const isEpharmacy = linkInfo?.custom?.custom_генерировать_бад_рацион_при_саморегистрации;
  const [isChild, setIsChild] = useState<boolean>(false);
  const [birthdayDate, setBirthdayDate] = useState<string>("");
  const hasCustomCities = !!linkInfo?.custom?.custom_города;
  const isCustomizationSchemeUsed: boolean = Boolean(linkInfo?.custom);

  /*Видимость полей через query params*/
  const fields: fields[] = ['name', 'lastname', 'midname', 'phone', 'email', 'birthday', 'gender', 'country', 'city', 'address']
  const params = new URLSearchParams(window.location.search);
  const queryVisibleFields = fields.filter(field => params.get(`${field}_visible`) !== null);

  let fieldsConfig: FieldsConfig = {
    name: {
      isRequired: true,
      isVisible: true
    },
    lastname: {
      isRequired: true,
      isVisible: true,
    },
    midname: {
      isRequired: false,
      isVisible: true,
    },
    phone: {
      isRequired: true,
      isVisible: true,
    },
    email: {
      isRequired: userType === UserType.PRO,
      isVisible: true,
    },
    birthday: {
      isVisible: true,
    },
    gender: {
      isVisible: true,
    },
    country:{
      isRequired: false,
      isVisible: true,
    },
    city:{
      isRequired: hasCustomCities,
      isVisible: true,
    },
    address:{
      isRequired: false,
      isVisible: true,
    },
  };

  if (isCustomizationSchemeUsed) {
    fieldsConfig = {
      name: {
        isRequired: linkInfo?.custom?.custom_обязательность_имя || false,
        isVisible: linkInfo?.custom?.custom_видимость_имя || false,
      },
      lastname: {
        isRequired: linkInfo?.custom?.custom_обязательность_фамилия || false,
        isVisible: linkInfo?.custom?.custom_видимость_фамилия || false,
      },
      midname: {
        isRequired: linkInfo?.custom?.custom_обязательность_отчество || false,
        isVisible: linkInfo?.custom?.custom_видимость_отчество || false,
      },
      phone: {
        isRequired: linkInfo?.custom?.custom_обязательность_телефон || false,
        isVisible: linkInfo?.custom?.custom_видимость_телефон || false,
      },
      email: {
        isRequired: linkInfo?.custom?.custom_обязательность_эл_почта || false,
        isVisible: linkInfo?.custom?.custom_видимость_эл_почта || false,
      },
      birthday: {
        isVisible: linkInfo?.custom?.custom_видимость_дата_рождения || false,
      },
      gender: {
        isVisible: linkInfo?.custom?.custom_видимость_пол || false,
      },
      country:{
        isRequired: linkInfo?.custom?.custom_обязательность_страна || false,
        isVisible: linkInfo?.custom?.custom_видимость_страна || false,
      },
      city:{
        isRequired: linkInfo?.custom?.custom_обязательность_город || false,
        isVisible: linkInfo?.custom?.custom_видимость_город || false,
      },
      address:{
        isRequired: linkInfo?.custom?.custom_обязательность_адрес || false,
        isVisible: linkInfo?.custom?.custom_видимость_адрес || false,
      },
    }
  }

  /*Меняем видимость полям, которые есть в query string*/
  queryVisibleFields.forEach((field: fields) => {
    fieldsConfig[field].isVisible = params.get(`${field}_visible`) === "true";
  })

  const { data: dataCountries = [] } = useSWR<
    {
      key: string;
      custom_название: string;
    }[]
  >([CATEGORIES.COUNTRY, 301]);
  const { data: dataCities = [] } = useSWR<
    {
      key: string;
      custom_название: string;
      custom_страна: { key: string } | null;
    }[]
  >([CATEGORIES.CITY, 300]);

  if (isEpharmacy) {
    const handleSubmit = ({ smoking, ...formValues }: FormValues) => {
      const illness = {
        [Smoking.YES]: ["c105a902t151r6091781"],
        [Smoking.NO]: [],
      };

      updatePatient({
        ...formValues,
        custom_болезни: illness[smoking],
        custom_firstname: patient?.custom_firstname || "ㅤ",
        custom_lastname: patient?.custom_lastname || "ㅤ",
        custom_телефон: patient?.custom_телефон || ""
      });

      setIsRegistrationDone && setIsRegistrationDone(true)
      props.onSubmit();
    };

    if(isInitial && isRegistrationDone) props.onSubmit();

    if (isLoading && isEpharmacy) {
      return (
        <PageLoader
          title={"Генерация отчёта и рекомендации"}
          description={"Это может занять некоторое время"}
          duration={45}
        />
      )
    }

    return (
      <Form<FormValues>
        initialValues={
          patient || {
            custom_масса_тела: "",
            custom_рост: "",
          }
        }
        validate={epharmacyFormValidator(fieldsConfig)}
        validateOnMount
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, valid, values, submitting }) => {
          return (
            <Screen
              title="Пару слов о себе"
              buttonTitle="Продолжить"
              onSubmit={handleSubmit}
              isInvalid={!valid}
              isLoading={submitting}
            >
              <div className={cx("disclaimer")}>
                <span className={cx("disclaimer__inner")}>
                  Заполните все поля
                </span>
              </div>
              <div className={cx("form-body")}>
                {fieldsConfig?.birthday?.isVisible &&
                  <Field name="custom_дата_рождения">
                    {({ input, meta }) => (
                      <InputDate
                        {...input}
                        label="Дата рождения"
                        required
                        error={meta.touched ? meta.error : undefined}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          const isChildEntered = getIsChild(event.target.value);
                          if (isChildEntered !== isChild) {
                            values.custom_жизненная_активность = "";
                            values.custom_спортивная_автивность = "";
                            values.custom_детская_жизненная_активность = "";
                            setIsChild(isChildEntered);
                          }
                          setBirthdayDate(event.target.value);
                          input.onChange(event.target.value);
                        }}
                        value={input.value}
                      />
                    )}
                  </Field>
                }
                {fieldsConfig?.gender?.isVisible &&
                  <Field name="custom_sexint_save">
                    {(props) => (
                      <div className={cx("m-radio")}>
                        <RadioGroup
                          name={props.input.name}
                          label="Пол"
                          required
                          items={radioGroupSex}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            props.input.onChange(event.target.value);
                          }}
                          value={props.input.value}
                        />
                      </div>
                    )}
                  </Field>
                }
                {(birthdayDate.split(".").join("").replace("_", "").length === 8 || !fieldsConfig?.birthday?.isVisible) &&
                  <AnthropometryFields isChild={isChild} />
                }
              </div>
            </Screen>
          );
        }}
      </Form >
    );
  }

  const handleSubmit = (formValues: FormValues) => {
    updatePatient(formValues);
    props.onSubmit();
  };



  return (
    <Form<FormValues>
      initialValues={{
        ...patient!,
        custom_страна:
          patient?.custom_страна || linkInfo?.custom?.custom_страны?.[0].key,
      }}
      validate={createValidation(fieldsConfig)}
      validateOnMount
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, valid, values, submitting }) => {
        const countries =
          linkInfo?.custom?.custom_страны?.map((item) => ({
            value: item.key,
            label: item.label,
          })) ||
          dataCountries.map((item) => ({
            value: item.key,
            label: item.custom_название,
          }));

        const cities =
          linkInfo?.custom?.custom_города?.map((item) => ({
            value: item.key,
            label: item.label,
          })) ||
          dataCities
            .filter((item) => item.custom_страна?.key === values.custom_страна)
            .map((item) => ({
              value: item.key,
              label: item.custom_название,
            }));

        return (
          <Screen
            title="Сейчас мы вас зарегистрируем"
            buttonTitle="Продолжить"
            onSubmit={handleSubmit}
            isInvalid={!valid}
            isLoading={submitting}
          >
            <>
              {linkInfo?.custom?.custom_показывать_звездочки_обязательности &&
                <div className={cx("disclaimer")}>
                  <span className={cx("disclaimer__inner")}>
                    Поля отмеченные звездочкой (
                    <span className={cx("required")}>
                      <span className={cx("red-star")}>*</span>
                    </span>
                    ) обязательны для заполнения
                  </span>
                </div>
              }
              <div className={cx("form-body")}>
                {fieldsConfig?.lastname?.isVisible &&
                  <FormField
                    name="custom_lastname"
                    label="Фамилия"
                    required = {fieldsConfig?.lastname?.isRequired}
                  />
                }
                {fieldsConfig?.name?.isVisible &&
                  <FormField
                    name="custom_firstname"
                    label="Имя"
                    required = {fieldsConfig?.name?.isRequired}
                  />
                }
                {fieldsConfig?.midname?.isVisible &&
                  <FormField
                    name="custom_midname"
                    label="Отчество"
                    required={fieldsConfig?.midname?.isRequired}
                  />
                }
                {fieldsConfig?.birthday?.isVisible &&
                  <Field name="custom_дата_рождения">
                    {({ input, meta }) => (
                      <InputDate
                        {...input}
                        label="Дата рождения"
                        required
                        error={meta.touched ? meta.error : undefined}
                      />
                    )}
                  </Field>
                }
                {fieldsConfig?.gender?.isVisible &&
                  <Field name="custom_sexint_save">
                    {(props) => (
                      <div className={cx("m-radio")}>
                        <RadioGroup
                          name={props.input.name}
                          label="Пол"
                          required
                          items={radioGroupSex}
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            props.input.onChange(event.target.value);
                          }}
                          value={props.input.value}
                        />
                      </div>
                    )}
                  </Field>
                }
                <div className={cx("disclaimer", "disclaimer-additional")}>
                  {linkInfo?.custom?.custom_заключение ?
                    <div
                      className={cx("disclaimer__inner")}
                      dangerouslySetInnerHTML={{
                        __html: linkInfo?.custom?.custom_заключение,
                      }}
                    />
                    :
                    <span className={cx("disclaimer__inner")}>
                      Почта и телефон нужны для связи консультанта с вами
                    </span>
                  }
                </div>
                {fieldsConfig?.email?.isVisible &&
                  <FormField
                    name="custom_login"
                    label={linkInfo?.custom?.custom_название_поля_эл_почта || "Эл.почта"}
                    required={fieldsConfig?.email?.isRequired}
                  />
                }
                {fieldsConfig?.phone?.isVisible &&
                  <Field name="custom_телефон">
                    {({ input, meta }) => (
                      <InputPhone
                        {...input}
                        label="Телефон"
                        required={fieldsConfig?.phone?.isRequired}
                        error={meta.touched ? meta.error : undefined}
                      />
                    )}
                  </Field>
                }
                {fieldsConfig?.country?.isVisible && countries.length > 1 && (
                  <Field name="custom_страна">
                    {({ input }) => (
                      <MobileSelect
                        searchable
                        options={countries}
                        placeholder="Страна"
                        required={fieldsConfig?.country?.isRequired}
                        {...input}
                      />
                    )}
                  </Field>
                )}
                {fieldsConfig?.city?.isVisible &&
                  <Field name="custom_город">
                    {({ input }) => (
                      <MobileSelect
                        required={fieldsConfig?.city?.isRequired}
                        searchable
                        disabled={!values.custom_страна}
                        options={cities}
                        extraOptions={
                          linkInfo?.custom?.custom_города
                            ? []
                            : dataCities
                              .filter((item) => !item.custom_страна)
                              .map((item) => ({
                                value: item.key,
                                label: item.custom_название,
                              }))
                        }
                        placeholder="Город"
                        {...input}
                      />
                    )}
                  </Field>
                }
                {fieldsConfig?.address?.isVisible &&
                  <FormField 
                    name="custom_адрес" 
                    label="Адрес" 
                    required={fieldsConfig?.address?.isRequired}
                  />
                }
              </div>
            </>
          </Screen>
        );
      }}
    </Form>
  );
};
